/**
 * CounterAssignPopover
 *
 * Appears after the user drag-selects (or presses and holds) a time range on an officer row, or taps a
 * single empty cell there. Mirrors the pen-and-paper flow: drag = underline the range, pick = write it.
 *
 * One step, whatever the range held: put the officer on a counter (or a free-text duty), give them a break
 * there ("#"), or — when the range has something on it — clear it, leaving them available but unassigned
 * (green). A range that held a counter opens on that counter, so a change is one pick.
 *
 * Start, end and the counter are all editable before anything is saved: a finger-drawn selection is often a
 * slot out, so the two time pickers are the correction, and nothing is written until Save is pressed.
 *
 * Counter picker is a native <select> unless the caller can say which counters are free (vacancyOf): then it
 * is the colour-coded sheet (green free / red held) that looks the same on every device. A native list cannot
 * colour its options on iOS, so otherwise the two kinds of choice are told apart by group: "Counters", then
 * "Non-counter deployments" (free text like "SOS DCAR" or "B@M CTRL BOOTH" — see utils/dutyOptions.ts).
 */

import { useEffect, useRef, useState } from "react";
import { TimeRangeSelect } from "@/components/TimeRangeSelect";
import { CounterChoiceSelect } from "@/components/CounterChoiceSelect";
import { NonCounterEffects } from "@/components/NonCounterEffects";
import { parseCounterChoice } from "@/utils/counterChoice";
import { getStartHour, hmmmToEndSlot, hmmmToSlot } from "@/config/modeConfig";
import { slotToHhmm } from "@/utils/partialTiming";
import type { CounterVacancy } from "@/utils/hwCounterVacancy";
import { PopoverShell } from "@/components/PopoverShell";
import { ActionNote, ActionTabs, PopoverFooter, PopoverHeader } from "@/components/PopoverParts";
import { fmtHhmm, isNarrow } from "@/utils/popoverLayout";
import { CELL_ACTION_LOOK, type CellAction, type CellActionKind } from "@/utils/cellAction";

interface CounterAssignPopoverProps {
  startTime: string;
  endTime: string;
  /** Shift the grid is showing — drives the time pickers' option list. */
  shiftType?: string;
  /**
   * Called when the user edits the range. The parent owns start/end (it also
   * repaints the grid highlight from them), so this component stays controlled.
   * Omit to render the range read-only.
   */
  onRangeChange?: (startTime: string, endTime: string) => void;
  /** Client-X of the mouseup/touchend event — used to position floating panel. */
  anchorX: number;
  /** Client-Y of the mouseup/touchend event — used to position floating panel. */
  anchorY: number;
  numCounters: number;
  operation_mode?: string;
  /** The range holds a counter, duty or break — Clear is offered, and the picker opens on `initialChoice`. */
  filled?: boolean;
  /** The choice the counter picker opens on (a counter number, or "duty:<text>" — see utils/counterChoice). */
  initialChoice?: string;
  /** Called with what the user chose for the range. */
  onAction: (action: CellAction) => Promise<void>;
  onCancel: () => void;
  errorMessage?: string;
  /**
   * Whether counter `counterNum` is free from `startTime` (green) or held (red) for this officer. When
   * given, the counter picker shows it on every device; omit for the plain list.
   */
  vacancyOf?: (counterNum: number, startTime: string) => CounterVacancy | undefined;
}

const POPOVER_W = 280;
const POPOVER_H = 330; // approximate — enough for viewport-edge clamping

export function CounterAssignPopover({
  startTime,
  endTime,
  shiftType = "morning",
  onRangeChange,
  anchorX,
  anchorY,
  numCounters,
  operation_mode,
  filled = false,
  initialChoice,
  onAction,
  onCancel,
  errorMessage,
  vacancyOf,
}: CounterAssignPopoverProps) {
  const selectRef = useRef<HTMLSelectElement>(null);
  // A 1-based counter number ("12"), or a duty as "duty:<text>".
  const [selected, setSelected] = useState(initialChoice || "1");
  const selectedDuty = parseCounterChoice(selected).duty ?? null;
  const actions: CellActionKind[] = filled ? ["counter", "break", "clear"] : ["counter", "break"];
  const [action, setAction] = useState<CellActionKind>("counter");
  const [busy, setBusy] = useState(false);

  const isMobile = isNarrow();

  // Focus the select on mount so the user can interact immediately
  useEffect(() => {
    selectRef.current?.focus();
  }, []);

  const startHour = getStartHour(shiftType);
  let neededUntilSlot: number | undefined;
  try { neededUntilSlot = endTime ? hmmmToEndSlot(endTime, startHour) : undefined; } catch { /* leave unset */ }
  const vacancyForPicker = vacancyOf
    ? (n: number) => {
        try { hmmmToSlot(startTime, startHour); } catch { return undefined; }
        return vacancyOf(n, startTime);
      }
    : undefined;

  const handleConfirm = async () => {
    setBusy(true);
    try {
      if (action === "counter") {
        await onAction({ kind: "counter", counter: parseCounterChoice(selected).counter, duty: selectedDuty ?? undefined });
      } else {
        await onAction({ kind: action as "break" | "clear" });
      }
    } finally {
      setBusy(false);
    }
  };

  const handleKey = (e: React.KeyboardEvent) => {
    // Enter on a button (the colour-coded counter picker's trigger) presses that button; it must not also save.
    if (e.key === "Enter" && (e.target as HTMLElement).tagName !== "BUTTON") handleConfirm();
    if (e.key === "Escape") onCancel();
  };

  const color = CELL_ACTION_LOOK[action].color;
  const verb = action === "break" ? "Break" : action === "clear" ? "Clear" : selectedDuty ? "Duty" : filled ? "Change counter" : "Assign counter";
  const range = `${fmtHhmm(startTime)} – ${fmtHhmm(endTime)}`;

  return (
    <PopoverShell anchorX={anchorX} anchorY={anchorY} width={POPOVER_W} approxHeight={POPOVER_H} onCancel={onCancel}>
      <div onKeyDown={handleKey}>
        <PopoverHeader title={`${verb} · ${range}`} color={color} busy={busy} onClose={onCancel} />

        {/* Range pickers — the highlight can be a slot out either side */}
        {onRangeChange && (
          <TimeRangeSelect
            shiftType={shiftType}
            start={startTime}
            end={endTime}
            onChange={onRangeChange}
            isMobile={isMobile}
            accent={action === "clear" ? color : undefined}
            disabled={busy}
          />
        )}

        <ActionTabs actions={actions} value={action} onChange={setAction} isMobile={isMobile} />

        {action === "counter" && (
          <>
            <CounterChoiceSelect
              ref={selectRef}
              value={selected}
              onChange={setSelected}
              numCounters={numCounters}
              operationMode={operation_mode}
              vacancyOf={vacancyForPicker}
              slotTime={vacancyForPicker ? (slot) => slotToHhmm(slot, startHour) : undefined}
              neededUntilSlot={neededUntilSlot}
              title={`Counter — ${startTime}–${endTime}`}
              style={{
                width: "100%",
                padding: isMobile ? "12px 14px" : "10px 12px",
                fontSize: isMobile ? 16 : 15,
                fontWeight: 500,
                borderRadius: 8,
                border: "1.5px solid #d1d5db",
                background: "white",
                appearance: "auto",
                marginBottom: selectedDuty ? 6 : 14,
                cursor: "pointer",
                boxSizing: "border-box",
              }}
            />
            {selectedDuty && (
              <div style={{ marginBottom: 14 }}>
                <NonCounterEffects fontSize={12} />
              </div>
            )}
          </>
        )}
        {action === "break" && <ActionNote color={color}>Break (#) for {range}</ActionNote>}
        {action === "clear" && <ActionNote color={color}>Clears {range}: available, but on nothing (green)</ActionNote>}

        <PopoverFooter action={action} busy={busy} error={errorMessage} onSave={handleConfirm} isMobile={isMobile} />
      </div>
    </PopoverShell>
  );
}
